import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowLeftIcon, ArrowRightIcon, IconButton, Tooltip } from "@activecollab/components";
import PropTypes from "prop-types";
import { useTaskPageNavigation } from "~/repository/useTaskPageNavigation";
import Angie from "~/utils/angie";

export const SingleTaskNavigation = ({ projectId, taskId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading, isFetched } = useTaskPageNavigation(projectId, taskId);

  const { next, prev } = data ?? {};

  const handlePrev = useCallback(() => {
    if (prev) {
      Angie.logger.info("Task navigation button clicked.", {
        direction: "prev",
        navigation_source: "single",
        navigation_type: "task",
      });
      navigate(`/projects/${projectId}/tasks/${prev}`);
    }
  }, [navigate, prev, projectId]);

  const handleNext = useCallback(() => {
    if (next) {
      Angie.logger.info("Task navigation button clicked.", {
        direction: "next",
        navigation_source: "single",
        navigation_type: "task",
      });
      navigate(`/projects/${projectId}/tasks/${next}`);
    }
  }, [navigate, next, projectId]);

  const showPrevNext = Boolean(prev || next);
  const loading = !isFetched || isLoading;

  if (!showPrevNext && !loading) return null;

  return (
    <span className="tw-pr-3 tw-mr-3 tw-border-0 tw-border-solid tw-border-border-primary tw-border-r tw-inline-flex">
      <Tooltip
        disable={loading}
        title={prev ? t("Previous Task") : t("You are at beginning of the list")}
      >
        <div>
          <IconButton
            disabled={!prev || loading}
            onClick={handlePrev}
            variant="text gray"
            className="tw-mr-2"
          >
            <ArrowLeftIcon />
          </IconButton>
        </div>
      </Tooltip>
      <Tooltip
        disable={loading}
        title={next ? t("Next Task") : t("You have reached the end of the list")}
      >
        <div>
          <IconButton
            disabled={!next || loading}
            onClick={handleNext}
            variant="text gray"
          >
            <ArrowRightIcon />
          </IconButton>
        </div>
      </Tooltip>
    </span>
  );
};

SingleTaskNavigation.propTypes = {
  projectId: PropTypes.number.isRequired,
  taskId: PropTypes.number.isRequired,
};
